import { matchPath } from "react-router";

/**
 * What session replay may show on each screen.
 *
 * - `open`: words are visible. Only screens that read the same for everyone:
 *   the welcome and sign-in steps, the paywall and plan, the settings.
 * - `masked`: every click, scroll and screen change is recorded, but every
 *   word is replaced with asterisks and every field is blanked. Birth details,
 *   readings written from the chart, anything said to or by Antar, friends'
 *   names, the email address.
 * - `off`: nothing is recorded at all. The sign-in callback, whose URL
 *   carries the sign-in secret.
 *
 * This is an allowlist, as the phone app's replay policy is: a screen added
 * later and forgotten here is `masked`, never `open`. Form fields are masked
 * on every screen, whatever this says.
 */
export type ScreenPolicy = "open" | "masked" | "off";

export interface Screen {
  /** The route pattern, never the real path: `/friends/:userId`, not an id. */
  readonly route: string;
  readonly policy: ScreenPolicy;
}

const SCREENS: ReadonlyArray<Screen> = [
  { route: "/", policy: "open" },
  { route: "/welcome", policy: "open" },
  { route: "/auth/callback", policy: "off" },
  // Birth date, time and place, and the email address, are typed here.
  { route: "/start", policy: "masked" },
  { route: "/start/date", policy: "masked" },
  { route: "/start/time", policy: "masked" },
  { route: "/start/place", policy: "masked" },
  // The first week's reading, written from their chart.
  { route: "/start/reveal", policy: "masked" },
  { route: "/start/consent", policy: "open" },
  { route: "/start/sign-in", policy: "open" },
  { route: "/start/email", policy: "masked" },
  { route: "/start/code", policy: "masked" },
  { route: "/start/talk", policy: "open" },
  { route: "/start/saving", policy: "open" },
  { route: "/subscribe", policy: "open" },
  { route: "/talk", policy: "open" },
  // Readings and dates written from their chart.
  { route: "/today", policy: "masked" },
  { route: "/life", policy: "masked" },
  // What people say to Antar and what she says back.
  { route: "/talk/ask", policy: "masked" },
  { route: "/talk/voice", policy: "masked" },
  { route: "/talk/summary", policy: "masked" },
  // Other people's names and charts.
  { route: "/friends", policy: "masked" },
  { route: "/friends/:userId", policy: "masked" },
  // The chart pages can show the birth details; readings are personal.
  { route: "/life/charts", policy: "masked" },
  { route: "/life/charts/:divisor", policy: "masked" },
  { route: "/life/transits", policy: "masked" },
  { route: "/life/remedies", policy: "masked" },
  { route: "/life/:reading", policy: "masked" },
  // The handle, the email and what Antar remembers.
  { route: "/you", policy: "masked" },
  { route: "/you/memory", policy: "masked" },
  { route: "/you/page", policy: "masked" },
  { route: "/you/delete", policy: "masked" },
  { route: "/you/plan", policy: "open" },
  { route: "/you/language", policy: "open" },
  { route: "/you/privacy", policy: "open" },
];

const UNKNOWN: Screen = Object.freeze({ route: "(unknown)", policy: "masked" });

/** The screen a path belongs to. Static routes win over `/life/:reading` and the like. */
export function screenFor(pathname: string): Screen {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const exact = SCREENS.find((s) => s.route === path);
  if (exact) return exact;
  return SCREENS.find((s) => s.route.includes(":") && matchPath({ path: s.route, end: true }, path)) ?? UNKNOWN;
}

/**
 * The attribute on `<html>` that replay's masking selector reads. Anything
 * without `data-replay="open"` on the page root has its words masked, so a
 * page that has not yet said what it is starts masked.
 */
export const REPLAY_ATTRIBUTE = "data-replay";
export const MASKED_TEXT_SELECTOR = `html:not([${REPLAY_ATTRIBUTE}="open"]), [data-private], [data-private] *`;
